import type { JSX } from 'react'
import { useAgents } from '../../../hooks/useAgents.js'
import { useContactCenterQueues } from '../../../hooks/useContactCenterQueues.js'
import { useNotificationTemplates } from '../../../hooks/useNotificationTemplates.js'
import type {
  NotifyAction,
  NotifyRecipient,
} from '../../../modules/admin/conversation-engine/types.js'
import Select from '../../ui/Select.js'

/**
 * Discriminated union of escalation actions (UI-2d Task 5, design spec §3.3).
 *
 * Mirrors the action shapes consumed by the EscalationEngine via
 *   apps/api/src/routes/conversations/admin/escalation-rules.ts
 * (CreateBodySchema.actions is z.array(z.record(z.unknown())) — untyped at the
 * API boundary, so the per-action handlers are the contract).
 *
 * Per §0.3 the `notify` action shares the SINGULAR-`recipient` shape with
 * AutomationActionPicker (the shared `NotifyAction` type) — NOT a plural
 * `recipients: Recipient[]` array as sketched in the spec body.
 */
export type EscalationAction =
  | NotifyAction
  | { type: 'reassign_to_agent'; agentId: string }
  | { type: 'reassign_to_queue'; queueId: string }
  | { type: 'increase_priority'; priorityId: string }
  | { type: 'add_watcher'; agentIds: string[] }

export interface EscalationActionPickerProps {
  value: EscalationAction[]
  onChange: (next: EscalationAction[]) => void
}

const EMPTY_AGENT_IDS: string[] = []

function blank(type: EscalationAction['type']): EscalationAction {
  switch (type) {
    case 'notify':
      return {
        type: 'notify',
        templateId: '',
        recipient: { type: 'agent', id: '' },
      }
    case 'reassign_to_agent':
      return { type: 'reassign_to_agent', agentId: '' }
    case 'reassign_to_queue':
      return { type: 'reassign_to_queue', queueId: '' }
    case 'increase_priority':
      return { type: 'increase_priority', priorityId: '' }
    case 'add_watcher':
      return { type: 'add_watcher', agentIds: [] }
  }
}

export default function EscalationActionPicker({
  value,
  onChange,
}: EscalationActionPickerProps): JSX.Element {
  const { agents } = useAgents()
  const { items: queues } = useContactCenterQueues()
  const { templates } = useNotificationTemplates({ audience: 'agent' })

  function updateAt(i: number, next: EscalationAction): void {
    onChange(value.map((a, idx) => (idx === i ? next : a)))
  }

  function removeAt(i: number): void {
    onChange(value.filter((_, idx) => idx !== i))
  }

  function addAction(): void {
    onChange([...value, blank('notify')])
  }

  return (
    <div className="eap flex flex-col gap-2">
      {value.length === 0 && (
        <p className="text-sm text-gray-500">No actions configured.</p>
      )}
      {value.map((action, i) => (
        <div
          key={i}
          role="group"
          aria-label={`Escalation action ${i + 1}`}
          className="eap-row flex flex-wrap items-center gap-2 rounded border border-gray-300 p-2"
        >
                    <Select
            ariaLabel={`Action type ${i + 1}`}
            size="sm"
            value={action.type}
            onChange={(v) => updateAt(i, blank(v as EscalationAction['type']))}
          >
            <option value="notify">Notify</option>
            <option value="reassign_to_agent">Reassign to agent</option>
            <option value="reassign_to_queue">Reassign to queue</option>
            <option value="increase_priority">Increase priority</option>
            <option value="add_watcher">Add watcher</option>
                    </Select>

          {action.type === 'notify' && (
            <NotifyEditor
              index={i}
              action={action}
              onChange={(next) => updateAt(i, next)}
              agents={agents}
              queues={queues}
              templates={templates}
            />
          )}

          {action.type === 'reassign_to_agent' && (
                        <Select
              ariaLabel={`Reassign agent ${i + 1}`}
              size="sm"
              value={action.agentId}
              onChange={(v) => updateAt(i, {
                  type: 'reassign_to_agent',
                  agentId: v,
                })}
            >
              <option value="">— select agent —</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.extension}
                </option>
              ))}
                        </Select>
          )}

          {action.type === 'reassign_to_queue' && (
                        <Select
              ariaLabel={`Reassign queue ${i + 1}`}
              size="sm"
              value={action.queueId}
              onChange={(v) => updateAt(i, {
                  type: 'reassign_to_queue',
                  queueId: v,
                })}
            >
              <option value="">— select queue —</option>
              {queues.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.name}
                </option>
              ))}
                        </Select>
          )}

          {action.type === 'increase_priority' && (
            // No priority-listing endpoint/hook exists in the codebase
            // (priorityId is a free UUID — see GET /admin/conversations
            // rule-fields, conversations/update). Plain text input until a
            // priorities source is added.
            <input
              aria-label={`Priority id ${i + 1}`}
              type="text"
              value={action.priorityId}
              placeholder="priority id"
              onChange={(e) =>
                updateAt(i, {
                  type: 'increase_priority',
                  priorityId: e.target.value,
                })
              }
              className="rounded border border-gray-300 px-1 py-0.5 text-xs"
            />
          )}

          {action.type === 'add_watcher' && (
            <select
              aria-label={`Watcher agents ${i + 1}`}
              multiple
              value={action.agentIds ?? EMPTY_AGENT_IDS}
              onChange={(e) => {
                const ids = Array.from(e.target.selectedOptions).map(
                  (o) => o.value,
                )
                updateAt(i, { type: 'add_watcher', agentIds: ids })
              }}
              className="rounded border border-gray-300 px-1 py-0.5 text-xs"
            >
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.extension}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            aria-label={`Remove action ${i + 1}`}
            className="ml-auto rounded border border-gray-300 px-2 py-0.5 text-xs"
            onClick={() => removeAt(i)}
          >
            Remove
          </button>
        </div>
      ))}

      <div>
        <button
          type="button"
          className="rounded border border-gray-300 px-2 py-1 text-xs"
          onClick={addAction}
        >
          + Add action
        </button>
      </div>
    </div>
  )
}

interface AgentLike {
  id: string
  extension: string
}
interface QueueLike {
  id: string
  name: string
}
interface TemplateLike {
  id: string
  eventKey: string
}

const NOTIFY_ROLES: Array<'supervisor' | 'admin' | 'manager'> = [
  'supervisor',
  'admin',
  'manager',
]

function NotifyEditor({
  index,
  action,
  onChange,
  agents,
  queues,
  templates,
}: {
  index: number
  action: NotifyAction
  onChange: (next: EscalationAction) => void
  agents: AgentLike[]
  queues: QueueLike[]
  templates: TemplateLike[]
}): JSX.Element {
  const recipientType = action.recipient.type

  function setRecipientType(t: string): void {
    let next: NotifyRecipient
    switch (t) {
      case 'agent':
        next = { type: 'agent', id: '' }
        break
      case 'queue':
        next = { type: 'queue', id: '' }
        break
      case 'role':
        next = { type: 'role', id: 'supervisor' }
        break
      case 'assignee':
        next = { type: 'assignee' }
        break
      case 'requester':
        next = { type: 'requester' }
        break
      default:
        return
    }
    onChange({ ...action, recipient: next })
  }

  return (
    <>
            <Select
        ariaLabel={`Notify template ${index + 1}`}
        size="sm"
        value={action.templateId}
        onChange={(v) => onChange({ ...action, templateId: v })}
      >
        <option value="">— select template —</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.eventKey}
          </option>
        ))}
            </Select>
            <Select
        ariaLabel={`Notify recipient type ${index + 1}`}
        size="sm"
        value={recipientType}
        onChange={(v) => setRecipientType(v)}
      >
        <option value="agent">Agent</option>
        <option value="queue">Queue</option>
        <option value="role">Role</option>
        <option value="assignee">Assignee</option>
        <option value="requester">Requester</option>
            </Select>
      {action.recipient.type === 'agent' && (
                <Select
          ariaLabel={`Notify agent ${index + 1}`}
          size="sm"
          value={action.recipient.id}
          onChange={(v) => onChange({
              ...action,
              recipient: { type: 'agent', id: v },
            })}
        >
          <option value="">— select agent —</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.extension}
            </option>
          ))}
                </Select>
      )}
      {action.recipient.type === 'queue' && (
                <Select
          ariaLabel={`Notify queue ${index + 1}`}
          size="sm"
          value={action.recipient.id}
          onChange={(v) => onChange({
              ...action,
              recipient: { type: 'queue', id: v },
            })}
        >
          <option value="">— select queue —</option>
          {queues.map((q) => (
            <option key={q.id} value={q.id}>
              {q.name}
            </option>
          ))}
                </Select>
      )}
      {action.recipient.type === 'role' && (
                <Select
          ariaLabel={`Notify role ${index + 1}`}
          size="sm"
          value={action.recipient.id}
          onChange={(v) => onChange({
              ...action,
              recipient: {
                type: 'role',
                id: v as 'supervisor' | 'admin' | 'manager',
              },
            })}
        >
          {NOTIFY_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
                </Select>
      )}
    </>
  )
}
